import { QuoteKind, QuoteSpec, QuoteType } from './types';
import { getQuoteBook, getResolutionMode, resolveQuoteValue, saveQuoteBook, type QuoteBookEntry } from './storage/quoteBook';
import { getSavedQuotes, saveAllQuotes } from './storage/quotes';

const MD_ENABLED_KEY = 'quantra_md_backend_enabled';
const MD_URL_KEY = 'quantra_md_backend_url';
const MD_LAST_SYNC_SNAPSHOT_ID_KEY = 'quantra_md_last_sync_snapshot_id';
const DEFAULT_MD_URL = import.meta.env.VITE_MARKET_DATA_API_URL || 'https://market.quantra.io';

interface MdStatusResponse {
  active: boolean;
  selected_snapshot_name: string | null;
  selected_snapshot_as_of: string | null;
}

interface MdSnapshotResponse {
  id: string;
  name: string;
  as_of: string;
}

interface MdSnapshotQuoteResponse {
  canonical_id: string;
  value: number;
}

interface MdSeriesResponse {
  canonical_id: string;
}

interface MdQuotePointResponse {
  canonical_id: string;
  as_of: string;
  value: number;
  source?: string | null;
  vendor_id?: string | null;
}

export interface MdBackendSettings {
  enabled: boolean;
  baseUrl: string;
}

export interface MdSyncResult {
  snapshotName: string;
  snapshotId: string;
  snapshotAsOf: string;
  importedQuotes: number;
}

export interface MdAsOfSyncResult {
  asOfDate: string;
  importedQuotes: number;
}

export interface MdResolveResult {
  asOfDate: string;
  foundQuotes: number;
  exactQuotes: number;
}

interface MdSeriesMonthResponse {
  canonical_id: string;
  year: number;
  month: number;
  total_points: number;
  returned_points: number;
  sampled: boolean;
  points: MdQuotePointResponse[];
}

interface MdResolvedItemResponse {
  canonical_id: string;
  requested_as_of: string;
  found: boolean;
  is_exact: boolean;
  resolved_as_of?: string | null;
  value?: number | null;
  source?: string | null;
  vendor_id?: string | null;
}

interface MdResolvedBatchResponse {
  items: MdResolvedItemResponse[];
}

interface MdMonthOverviewDay {
  date: string;
  total_series: number;
  exact_count: number;
  resolved_count: number;
  color: 'red' | 'yellow' | 'green';
}

export interface MdMonthOverview {
  year: number;
  month: number;
  total_series: number;
  days: MdMonthOverviewDay[];
}

export interface PricingQuoteSnapshotItem {
  id: string;
  kind: string;
  value: number;
  quote_type?: QuoteType;
}

export interface MdPricingResolveResult {
  quotes: PricingQuoteSnapshotItem[];
  localHits: number;
  backendHits: number;
  missingIds: string[];
}

type Point = { date: string; value: number };
const monthSeriesCache = new Map<string, Point[]>();
const monthOverviewCache = new Map<string, MdMonthOverview>();
const backendSeriesIdsCache = new Map<string, string[]>();

function reduceSeriesForStorage(
  series: Array<{ date: string; value: number }>,
  maxPoints: number = 1500,
  recentKeep: number = 750
): Array<{ date: string; value: number }> {
  if (series.length <= maxPoints) return series;

  const keepRecent = Math.min(recentKeep, maxPoints);
  const keepOlder = Math.max(0, maxPoints - keepRecent);
  const recent = series.slice(-keepRecent);
  const older = series.slice(0, Math.max(0, series.length - keepRecent));

  if (keepOlder === 0 || older.length === 0) return recent;

  const sampledOlder: Array<{ date: string; value: number }> = [];
  const step = Math.max(1, Math.floor(older.length / keepOlder));
  for (let i = 0; i < older.length && sampledOlder.length < keepOlder; i += step) {
    sampledOlder.push(older[i]);
  }

  return [...sampledOlder, ...recent].sort((a, b) => a.date.localeCompare(b.date));
}

function normalizeBaseUrl(baseUrl: string): string {
  const trimmed = baseUrl.trim();
  const noSlash = trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
  // Prevent mixed-content failures when app runs on HTTPS.
  if (noSlash.startsWith('http://')) {
    const host = noSlash.slice('http://'.length).split('/')[0].toLowerCase();
    const isLocal =
      host.startsWith('localhost') ||
      host.startsWith('127.0.0.1') ||
      host.startsWith('10.') ||
      host.startsWith('192.168.') ||
      /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(host);
    if (!isLocal) return `https://${noSlash.slice('http://'.length)}`;
  }
  return noSlash;
}

async function fetchJson<T>(baseUrl: string, path: string): Promise<T> {
  const response = await fetch(`${normalizeBaseUrl(baseUrl)}${path}`);
  if (!response.ok) {
    throw new Error(`Market data backend request failed (${response.status}) for ${path}`);
  }
  return (await response.json()) as T;
}

async function postJson<T>(baseUrl: string, path: string, payload: unknown): Promise<T> {
  const response = await fetch(`${normalizeBaseUrl(baseUrl)}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`Market data backend request failed (${response.status}) for ${path}`);
  }
  return (await response.json()) as T;
}

function inferQuoteKind(canonicalId: string): QuoteKind {
  const upper = canonicalId.toUpperCase();
  if (upper.includes('RATE') || upper.includes('YIELD')) return 'Rate';
  if (upper.includes('SPREAD')) return 'Spread';
  return 'Price';
}

function normalizeQuoteKind(raw: string | undefined, canonicalId: string): QuoteKind {
  if (raw === 'Rate' || raw === 'Spread' || raw === 'Price' || raw === 'FxSpot' || raw === 'FxPoints') {
    return raw;
  }
  return inferQuoteKind(canonicalId);
}

function toDisplayLabelFromCanonicalId(canonicalId: string): string {
  const parts = canonicalId.split('.');
  if (parts.length < 6) return canonicalId;
  const [currency, _asset, family, instrument, tenor, field] = parts;
  return `${currency} ${family} ${instrument} ${tenor} ${field}`;
}

function inferCurrency(canonicalId: string): string | undefined {
  const [prefix] = canonicalId.split('.');
  if (prefix && prefix.length === 3) return prefix;
  return undefined;
}

function normalizeSyncedValue(kind: QuoteKind, value: number): number {
  // Defensive normalization: if backend snapshot still contains percent values
  // (e.g. 3.65 for 3.65%), convert to decimal for portal conventions.
  if ((kind === 'Rate' || kind === 'Spread') && Math.abs(value) > 1) {
    return value / 100.0;
  }
  return value;
}

function monthKey(canonicalId: string, year: number, month: number, maxPoints: number): string {
  return `${canonicalId}|${year}-${String(month).padStart(2, '0')}|${maxPoints}`;
}

function dbMonthOverviewKey(baseUrl: string, year: number, month: number): string {
  return `db|${normalizeBaseUrl(baseUrl)}|${year}-${String(month).padStart(2, '0')}`;
}

export function getMdBackendSettings(): MdBackendSettings {
  const rawEnabled = localStorage.getItem(MD_ENABLED_KEY);
  const enabled = rawEnabled === null ? true : rawEnabled === 'true';
  const rawBaseUrl = localStorage.getItem(MD_URL_KEY) || DEFAULT_MD_URL;
  const normalizedBaseUrl = normalizeBaseUrl(rawBaseUrl);
  // Self-heal missing/legacy local settings so HTTPS pages can always issue
  // backend fetches (and avoid mixed-content blocks from http URLs).
  if (rawEnabled === null) {
    localStorage.setItem(MD_ENABLED_KEY, 'true');
  }
  if (!localStorage.getItem(MD_URL_KEY) || normalizedBaseUrl !== rawBaseUrl) {
    localStorage.setItem(MD_URL_KEY, normalizedBaseUrl);
  }
  return { enabled, baseUrl: normalizedBaseUrl };
}

export function saveMdBackendSettings(settings: MdBackendSettings): void {
  localStorage.setItem(MD_ENABLED_KEY, String(settings.enabled));
  localStorage.setItem(MD_URL_KEY, normalizeBaseUrl(settings.baseUrl));
}

export async function getMdBackendStatus(baseUrl: string): Promise<MdStatusResponse> {
  return fetchJson<MdStatusResponse>(baseUrl, '/status');
}

export async function syncLatestSnapshotToLocalQuoteBook(baseUrl: string): Promise<MdSyncResult> {
  const status = await getMdBackendStatus(baseUrl);
  if (!status.selected_snapshot_name) {
    throw new Error('No snapshot configured on backend (/status returned no selected_snapshot_name).');
  }

  const latestSnapshot = await fetchJson<MdSnapshotResponse>(
    baseUrl,
    `/snapshots/latest?name=${encodeURIComponent(status.selected_snapshot_name)}`
  );
  const snapshotQuotes = await fetchJson<MdSnapshotQuoteResponse[]>(
    baseUrl,
    `/snapshots/${encodeURIComponent(latestSnapshot.id)}/quotes?limit=50000`
  );

  // Pull full historical series for each snapshot canonical id so local Quote Book
  // can render full curves rather than only the latest snapshot point.
  const historyStart = '1900-01-01T00:00:00Z';
  const historyByCanonicalId = new Map<string, Array<{ date: string; value: number }>>();
  const historyMaxPoints = 400;
  const previousSnapshotId = localStorage.getItem(MD_LAST_SYNC_SNAPSHOT_ID_KEY);
  const hasExistingBook = getQuoteBook().length > 0;
  const shouldFetchHistory = !hasExistingBook || previousSnapshotId !== latestSnapshot.id;

  if (shouldFetchHistory) {
    await Promise.all(
      snapshotQuotes.map(async (quote) => {
        const id = quote.canonical_id;
        try {
          const series = await fetchJson<MdQuotePointResponse[]>(
            baseUrl,
            `/series/${encodeURIComponent(id)}?start=${encodeURIComponent(historyStart)}&end=${encodeURIComponent(
              latestSnapshot.as_of
            )}&limit=50000&max_points=${historyMaxPoints}`
          );
          historyByCanonicalId.set(
            id,
            series.map((point) => ({
              date: point.as_of.slice(0, 10),
              value: normalizeSyncedValue(inferQuoteKind(id), point.value),
            }))
          );
        } catch {
          // Keep sync resilient: if one history request fails, we'll still import latest point.
        }
      })
    );
  }

  const asOfDate = latestSnapshot.as_of.slice(0, 10);

  const bookEntries = getQuoteBook();
  const bookById = new Map(bookEntries.map((entry) => [entry.id, entry]));

  const flatQuotes = getSavedQuotes();
  const flatById = new Map(flatQuotes.map((quote) => [quote.id, quote]));

  for (const quote of snapshotQuotes) {
    const id = quote.canonical_id;
    const kind = inferQuoteKind(id);
    const currency = inferCurrency(id);
    const normalizedValue = normalizeSyncedValue(kind, quote.value);
    const generatedLabel = toDisplayLabelFromCanonicalId(id);

    const existingEntry = bookById.get(id);
    const nextEntry: QuoteBookEntry = existingEntry
      ? { ...existingEntry }
      : {
          id,
          kind,
          quote_type: 'Curve',
          label: generatedLabel,
          currency,
          description: `Synced from market data backend (${latestSnapshot.name})`,
          series: [],
        };

    const fullHistory = historyByCanonicalId.get(id);
    if (fullHistory && fullHistory.length > 0) {
      // Browser localStorage is limited; keep dense recent history and sampled older points.
      nextEntry.series = reduceSeriesForStorage(fullHistory);
    } else {
      const existingDateIdx = nextEntry.series.findIndex((point) => point.date === asOfDate);
      if (existingDateIdx >= 0) {
        nextEntry.series[existingDateIdx].value = normalizedValue;
      } else {
        nextEntry.series.push({ date: asOfDate, value: normalizedValue });
      }
      nextEntry.series.sort((a, b) => a.date.localeCompare(b.date));
    }
    bookById.set(id, nextEntry);

    const existingFlat = flatById.get(id);
    const nextFlat: QuoteSpec = {
      id,
      kind,
      quote_type: 'Curve',
      value: normalizedValue,
      label: existingFlat?.label || generatedLabel,
      currency: existingFlat?.currency || currency,
      description:
        existingFlat?.description || `Synced from market data backend (${latestSnapshot.name})`,
      createdAt: existingFlat?.createdAt,
      updatedAt: new Date().toISOString(),
    };
    flatById.set(id, nextFlat);
  }

  const bookToSave = Array.from(bookById.values());
  const quotesToSave = Array.from(flatById.values());
  try {
    saveQuoteBook(bookToSave);
    saveAllQuotes(quotesToSave);
    localStorage.setItem(MD_LAST_SYNC_SNAPSHOT_ID_KEY, latestSnapshot.id);
  } catch {
    // If storage is full/corrupted, fall back to single-point snapshot import
    // so Quote Book at least remains populated and usable.
    for (const entry of bookToSave) {
      if (entry.series.length > 1) {
        entry.series = [entry.series[entry.series.length - 1]];
      }
    }
    saveQuoteBook(bookToSave);
    saveAllQuotes(quotesToSave);
    localStorage.setItem(MD_LAST_SYNC_SNAPSHOT_ID_KEY, latestSnapshot.id);
  }

  return {
    snapshotName: latestSnapshot.name,
    snapshotId: latestSnapshot.id,
    snapshotAsOf: latestSnapshot.as_of,
    importedQuotes: snapshotQuotes.length,
  };
}

export async function hydrateMonthSeriesForQuoteBook(
  baseUrl: string,
  year: number,
  month: number,
  maxPoints: number = 400
): Promise<{ updatedSeries: number }> {
  const entries = getQuoteBook();
  if (entries.length === 0) return { updatedSeries: 0 };

  const byId = new Map(entries.map((entry) => [entry.id, { ...entry, series: [...entry.series] }]));
  let updatedSeries = 0;

  await Promise.all(
    Array.from(byId.keys()).map(async (id) => {
      const cacheKey = monthKey(id, year, month, maxPoints);
      let points = monthSeriesCache.get(cacheKey);
      if (!points) {
        try {
          const monthPayload = await fetchJson<MdSeriesMonthResponse>(
            baseUrl,
            `/series/${encodeURIComponent(id)}/month?year=${year}&month=${month}&max_points=${maxPoints}`
          );
          const kind = inferQuoteKind(id);
          points = monthPayload.points.map((point) => ({
            date: point.as_of.slice(0, 10),
            value: normalizeSyncedValue(kind, point.value),
          }));
          monthSeriesCache.set(cacheKey, points);
        } catch {
          // Some local quote IDs may not exist in backend; skip without failing all hydration.
          return;
        }
      }

      const entry = byId.get(id);
      if (!entry || points.length === 0) return;

      const seriesByDate = new Map(entry.series.map((p) => [p.date, p.value]));
      for (const p of points) seriesByDate.set(p.date, p.value);
      entry.series = Array.from(seriesByDate.entries())
        .map(([date, value]) => ({ date, value }))
        .sort((a, b) => a.date.localeCompare(b.date));
      byId.set(id, entry);
      updatedSeries += 1;
    })
  );

  saveQuoteBook(Array.from(byId.values()));
  return { updatedSeries };
}

export async function prefetchNeighborMonthsForQuoteBook(
  baseUrl: string,
  year: number,
  month: number,
  maxPoints: number = 400
): Promise<void> {
  const entries = getQuoteBook();
  if (entries.length === 0) return;

  const prev = month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
  const next = month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
  const targets = [prev, { year, month }, next];

  await Promise.all(
    targets.flatMap((target) =>
      entries.map(async (entry) => {
        const key = monthKey(entry.id, target.year, target.month, maxPoints);
        if (monthSeriesCache.has(key)) return;
        try {
          const payload = await fetchJson<MdSeriesMonthResponse>(
            baseUrl,
            `/series/${encodeURIComponent(entry.id)}/month?year=${target.year}&month=${target.month}&max_points=${maxPoints}`
          );
          const kind = inferQuoteKind(entry.id);
          monthSeriesCache.set(
            key,
            payload.points.map((point) => ({
              date: point.as_of.slice(0, 10),
              value: normalizeSyncedValue(kind, point.value),
            }))
          );
        } catch {
          // Ignore prefetch failures.
        }
      })
    )
  );
}

export async function getMonthOverviewForQuoteBook(baseUrl: string, year: number, month: number): Promise<MdMonthOverview> {
  const key = dbMonthOverviewKey(baseUrl, year, month);
  const cached = monthOverviewCache.get(key);
  if (cached) return cached;

  const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
  let ids = backendSeriesIdsCache.get(normalizedBaseUrl);
  if (!ids) {
    const series = await fetchJson<MdSeriesResponse[]>(baseUrl, '/series?limit=5000');
    ids = series.map((s) => s.canonical_id).filter(Boolean);
    backendSeriesIdsCache.set(normalizedBaseUrl, ids);
  }
  if (!ids || ids.length === 0) {
    return { year, month, total_series: 0, days: [] };
  }

  const overview = await postJson<MdMonthOverview>(baseUrl, '/month/overview', {
    canonical_ids: ids,
    year,
    month,
  });
  monthOverviewCache.set(key, overview);
  return overview;
}

export async function resolveAsOfForQuoteBook(baseUrl: string, asOfDate: string): Promise<MdResolveResult> {
  const entries = getQuoteBook();
  const ids = entries.map((e) => e.id);
  if (ids.length === 0) return { asOfDate, foundQuotes: 0, exactQuotes: 0 };

  const asOfIso = `${asOfDate}T00:00:00Z`;
  const resolved = await postJson<MdResolvedBatchResponse>(baseUrl, '/quotes/resolved', {
    canonical_ids: ids,
    as_of: asOfIso,
  });

  const byId = new Map(entries.map((entry) => [entry.id, { ...entry, series: [...entry.series] }]));
  const flatQuotes = getSavedQuotes();
  const flatById = new Map(flatQuotes.map((q) => [q.id, q]));

  let foundQuotes = 0;
  let exactQuotes = 0;
  for (const item of resolved.items) {
    if (!item.found || item.value == null || !item.resolved_as_of) continue;
    const entry = byId.get(item.canonical_id);
    if (!entry) continue;
    const kind = inferQuoteKind(item.canonical_id);
    const v = normalizeSyncedValue(kind, item.value);
    const d = item.resolved_as_of.slice(0, 10);
    const idx = entry.series.findIndex((p) => p.date === d);
    if (idx >= 0) entry.series[idx].value = v;
    else entry.series.push({ date: d, value: v });
    entry.series.sort((a, b) => a.date.localeCompare(b.date));
    byId.set(item.canonical_id, entry);

    const existingFlat = flatById.get(item.canonical_id);
    flatById.set(item.canonical_id, {
      id: item.canonical_id,
      kind,
      quote_type: 'Curve',
      value: v,
      label: existingFlat?.label || toDisplayLabelFromCanonicalId(item.canonical_id),
      currency: existingFlat?.currency || inferCurrency(item.canonical_id),
      description: existingFlat?.description || `Resolved from market data backend as_of ${asOfDate}`,
      createdAt: existingFlat?.createdAt,
      updatedAt: new Date().toISOString(),
    });

    foundQuotes += 1;
    if (item.is_exact) exactQuotes += 1;
  }

  saveQuoteBook(Array.from(byId.values()));
  saveAllQuotes(Array.from(flatById.values()));
  return { asOfDate, foundQuotes, exactQuotes };
}

export async function syncAsOfDateToLocalQuoteBook(baseUrl: string, asOfDate: string): Promise<MdAsOfSyncResult> {
  const resolved = await resolveAsOfForQuoteBook(baseUrl, asOfDate);
  return { asOfDate: resolved.asOfDate, importedQuotes: resolved.foundQuotes };
}

export async function buildPricingQuoteSnapshotWithBackend(
  asOfDate: string,
  opts?: {
    quoteType?: QuoteType | 'Any';
    quoteIds?: string[];
    preferBackend?: boolean;
  }
): Promise<MdPricingResolveResult> {
  const entries = getQuoteBook();
  const quoteType = opts?.quoteType || 'Curve';
  const idFilter = opts?.quoteIds ? new Set(opts.quoteIds) : null;
  const globalMode = getResolutionMode();

  const selected = entries.filter((entry) => {
    const effectiveType = entry.quote_type || 'Curve';
    if (quoteType !== 'Any' && effectiveType !== quoteType) return false;
    if (idFilter && !idFilter.has(entry.id)) return false;
    return true;
  });

  const quotes: PricingQuoteSnapshotItem[] = [];
  const selectedById = new Map(selected.map((entry) => [entry.id, entry]));
  let localHits = 0;
  for (const entry of selected) {
    const mode = entry.resolution_mode || globalMode;
    const localValue = resolveQuoteValue(entry.series, asOfDate, mode);
    if (localValue !== null) {
      quotes.push({
        id: entry.id,
        kind: entry.kind || inferQuoteKind(entry.id),
        value: localValue,
        ...(entry.quote_type ? { quote_type: entry.quote_type } : {}),
      });
      localHits += 1;
    }
  }

  const md = getMdBackendSettings();
  const preferBackend = opts?.preferBackend !== false;
  if (!md.enabled || !md.baseUrl) {
    const localIds = new Set(quotes.map((q) => q.id));
    const missingIds = selected.filter((entry) => !localIds.has(entry.id)).map((entry) => entry.id);
    return { quotes, localHits, backendHits: 0, missingIds };
  }

  const asOfIso = `${asOfDate}T00:00:00Z`;
  let resolved: MdResolvedBatchResponse;
  try {
    resolved = await postJson<MdResolvedBatchResponse>(md.baseUrl, '/quotes/resolved', {
      canonical_ids: selected.map((entry) => entry.id),
      as_of: asOfIso,
    });
  } catch (err) {
    if (!preferBackend) {
      const localIds = new Set(quotes.map((q) => q.id));
      const missingIds = selected.filter((entry) => !localIds.has(entry.id)).map((entry) => entry.id);
      return { quotes, localHits, backendHits: 0, missingIds };
    }
    throw err;
  }

  const byId = new Map(getQuoteBook().map((entry) => [entry.id, { ...entry, series: [...entry.series] }]));
  const flatById = new Map(getSavedQuotes().map((q) => [q.id, q]));
  const resolvedQuotes: PricingQuoteSnapshotItem[] = [];
  const missingIds = new Set<string>();
  let backendHits = 0;

  for (const item of resolved.items) {
    if (!item.found || item.value == null || !item.resolved_as_of) {
      missingIds.add(item.canonical_id);
      continue;
    }
    const entry = byId.get(item.canonical_id) || selectedById.get(item.canonical_id);
    const v = normalizeSyncedValue(inferQuoteKind(item.canonical_id), item.value);
    const d = item.resolved_as_of.slice(0, 10);
    if (entry) {
      const idx = entry.series.findIndex((p) => p.date === d);
      if (idx >= 0) entry.series[idx].value = v;
      else entry.series.push({ date: d, value: v });
      entry.series.sort((a, b) => a.date.localeCompare(b.date));
      byId.set(item.canonical_id, entry);
    }

    const existingFlat = flatById.get(item.canonical_id);
    const resolvedKind = normalizeQuoteKind(entry?.kind, item.canonical_id);
    flatById.set(item.canonical_id, {
      id: item.canonical_id,
      kind: resolvedKind,
      quote_type: entry?.quote_type || 'Curve',
      value: v,
      label: existingFlat?.label || toDisplayLabelFromCanonicalId(item.canonical_id),
      currency: existingFlat?.currency || inferCurrency(item.canonical_id),
      description: existingFlat?.description || `Resolved from market data backend as_of ${asOfDate}`,
      createdAt: existingFlat?.createdAt,
      updatedAt: new Date().toISOString(),
    });

    resolvedQuotes.push({
      id: item.canonical_id,
      kind: resolvedKind,
      value: v,
      ...(entry?.quote_type ? { quote_type: entry.quote_type } : {}),
    });
    backendHits += 1;
  }

  let finalQuotes = resolvedQuotes;
  if (!preferBackend) {
    const byIdMerged = new Map<string, PricingQuoteSnapshotItem>();
    for (const q of resolvedQuotes) byIdMerged.set(q.id, q);
    for (const q of quotes) {
      if (!byIdMerged.has(q.id)) byIdMerged.set(q.id, q);
    }
    finalQuotes = Array.from(byIdMerged.values());
  }

  saveQuoteBook(Array.from(byId.values()));
  saveAllQuotes(Array.from(flatById.values()));
  return { quotes: finalQuotes, localHits, backendHits, missingIds: Array.from(missingIds) };
}

